package com.islamicmedia.shared.data.ocr

import com.islamicmedia.shared.data.api.AnthropicApiClient
import com.islamicmedia.shared.domain.model.BulkItemStatus
import com.islamicmedia.shared.domain.model.BulkScanItem
import io.ktor.http.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

@Serializable
private data class SpineScanRequest(val imageBase64: String, val mimeType: String)

@Serializable
private data class SpineMatchDto(
    val title: String = "",
    val author: String = "",
    val year: String? = null,
    val coverUrl: String? = null
)

@Serializable
private data class SpineDto(
    val id: String = "",
    val index: Int = 0,
    val title: String = "",
    val author: String = "",
    val status: String = "unverified",
    val match: SpineMatchDto? = null,
    val copies: Int = 1,
    val thumbBase64: String? = null
)

@Serializable
private data class SpineScanResponse(val spines: List<SpineDto> = emptyList(), val error: String? = null)

/** One thing the streaming scan reports. */
sealed interface SpineScanEvent {
    /** The first pass located this many spines in the photo (the rows follow as each is identified). */
    data class Found(val count: Int) : SpineScanEvent
    /** A book is ready, or an earlier row with the same id improved (a rescued read, a catalog match). */
    data class Row(val item: BulkScanItem) : SpineScanEvent
    /** These rows were the same book: keep [keep] (now [copies] copies) and drop the others. */
    data class Merge(val keep: String, val drop: List<String>, val copies: Int) : SpineScanEvent
    data object Done : SpineScanEvent
    data class Failed(val message: String) : SpineScanEvent
}

sealed interface SpineScanOutcome {
    data class Found(val items: List<BulkScanItem>) : SpineScanOutcome
    /** Nothing that looks like a book spine in the photo. */
    data object NoBooks : SpineScanOutcome
    data class Failed(val message: String) : SpineScanOutcome
}

/**
 * Reads every book spine in one photo via the server's `/api/extract-spines` and returns review rows.
 * Works on Android, iOS and web through the server (same as [TextRecognizer]).
 */
class SpineScanner(private val api: AnthropicApiClient) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    @OptIn(ExperimentalEncodingApi::class)
    suspend fun scan(imageBytes: ByteArray): SpineScanOutcome {
        return try {
            val body = json.encodeToString(
                SpineScanRequest.serializer(),
                SpineScanRequest(Base64.encode(imageBytes), mimeTypeOf(imageBytes))
            )
            val (status, text) = api.authedCall(HttpMethod.Post, "/api/extract-spines", body)
                ?: return SpineScanOutcome.Failed("Couldn't reach the server. Check your connection and try again.")
            when {
                status == 429 ->
                    SpineScanOutcome.Failed("You've scanned a lot of photos in the last hour. Please try again a little later.")
                status == 401 -> SpineScanOutcome.Failed("Please sign in again to scan.")
                status == 403 -> SpineScanOutcome.Failed("Scanning a stack of books is only available on library accounts.")
                status !in 200..299 -> SpineScanOutcome.Failed("The scan didn't work. Please try again.")
                else -> {
                    val parsed = json.decodeFromString<SpineScanResponse>(text)
                    val items = parsed.spines.mapIndexed { i, s -> s.toItem("s$i") }
                    if (items.isEmpty()) SpineScanOutcome.NoBooks else SpineScanOutcome.Found(items)
                }
            }
        } catch (e: Exception) {
            SpineScanOutcome.Failed("Couldn't reach the server. Check your connection and try again.")
        }
    }


    /**
     * Reads the photo and reports each book the moment it is identified, so the list can fill in while
     * the rest are still being read. Never throws: any trouble ends the flow with [SpineScanEvent.Failed].
     */
    @OptIn(ExperimentalEncodingApi::class)
    fun scanStream(imageBytes: ByteArray): kotlinx.coroutines.flow.Flow<SpineScanEvent> = kotlinx.coroutines.flow.flow {
        val body = json.encodeToString(
            SpineScanRequest.serializer(),
            SpineScanRequest(Base64.encode(imageBytes), mimeTypeOf(imageBytes))
        )
        var finished = false
        try {
            api.startSpineScanSse(body).collect { ev ->
                val obj = runCatching { json.parseToJsonElement(ev.data) as? kotlinx.serialization.json.JsonObject }.getOrNull()
                when (ev.event) {
                    "found" -> emit(SpineScanEvent.Found(obj?.get("count")?.toString()?.toIntOrNull() ?: 0))
                    "row" -> runCatching { json.decodeFromString<SpineDto>(ev.data) }.getOrNull()
                        ?.let { emit(SpineScanEvent.Row(it.toItem(it.id.ifBlank { "r${it.index}" }, it.index))) }
                    "merge" -> if (obj != null) {
                        emit(SpineScanEvent.Merge(
                            keep = (obj["keep"] as? kotlinx.serialization.json.JsonPrimitive)?.content.orEmpty(),
                            drop = (obj["drop"] as? kotlinx.serialization.json.JsonArray)
                                ?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }.orEmpty(),
                            copies = obj["copies"]?.toString()?.toIntOrNull() ?: 1
                        ))
                    }
                    "done" -> { finished = true; emit(SpineScanEvent.Done) }
                    "error" -> {
                        finished = true
                        val code = (obj?.get("code") as? kotlinx.serialization.json.JsonPrimitive)?.content
                        emit(SpineScanEvent.Failed(when (code) {
                            "bulk_scan_not_allowed" -> "Scanning a stack of books isn't switched on for your account."
                            "library_only" -> "Scanning a stack of books is only available on library accounts."
                            "auth_required" -> "Please sign in again to scan."
                            "rate_limited" -> "You've scanned a lot of photos in the last hour. Please try again a little later."
                            else -> "The scan didn't work. Please try again."
                        }))
                    }
                    // "ping" and anything we don't know about: ignore.
                }
            }
            if (!finished) emit(SpineScanEvent.Failed("The connection dropped while reading. Please try again."))
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            emit(SpineScanEvent.Failed("Couldn't reach the server. Check your connection and try again."))
        }
    }

    private fun SpineDto.toItem(id: String, position: Int = 0): BulkScanItem {
        val st = when (status) {
            "matched" -> BulkItemStatus.MATCHED
            "check" -> BulkItemStatus.CHECK
            "unverified" -> BulkItemStatus.UNVERIFIED
            "guess" -> BulkItemStatus.GUESS
            "unreadable" -> BulkItemStatus.UNREADABLE
            else -> BulkItemStatus.UNVERIFIED
        }
        return BulkScanItem(
            id = id,
            title = (match?.title ?: title).trim(),
            author = (match?.author?.takeIf { it.isNotBlank() } ?: author).trim(),
            year = match?.year,
            coverUrl = match?.coverUrl,
            status = st,
            copies = copies.coerceAtLeast(1),
            thumbBase64 = thumbBase64,
            // Only a confident catalog match starts ticked; everything else needs the user's say-so.
            selected = st == BulkItemStatus.MATCHED,
            position = position
        )
    }

    private fun mimeTypeOf(b: ByteArray): String = when {
        b.size >= 3 && b[0] == 0xFF.toByte() && b[1] == 0xD8.toByte() && b[2] == 0xFF.toByte() -> "image/jpeg"
        b.size >= 4 && b[0] == 0x89.toByte() && b[1] == 0x50.toByte() && b[2] == 0x4E.toByte() && b[3] == 0x47.toByte() -> "image/png"
        else -> "image/jpeg"
    }
}
